import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'

import { formatCpfCnpj } from 'utils'

import { theme } from 'styles/theme'

import { ConflictRecord, STATUS_LABEL } from './types'

type ConflictRecordListProps = {
  records: ConflictRecord[]
  selectedUuid?: string
  onSelect?: (uuid: string) => void
  // Por padrão só há o que escolher quando existe mais de um cadastro. A tela de
  // edição passa false: lá o dialog é informativo, não tem ação sobre a seleção.
  selectable?: boolean
  // Isola o radiogroup quando duas listas coexistem na mesma página.
  radioName?: string
}

// Cards dos cadastros que já usam o documento. Extraído do dialog de duplicidade
// do cadastro manual (SC-10606) para a tela de edição usar a mesma apresentação
// (SC-10790): o conflito é o mesmo, muda só o que se pode fazer com ele.
const ConflictRecordList = ({
  records,
  selectedUuid,
  onSelect,
  selectable,
  radioName = 'cadastro-duplicado'
}: ConflictRecordListProps) => {
  const canSelect = selectable ?? records.length > 1

  return (
    /* Com cinco cadastros de nome idêntico o seletor tinha dois problemas:
       a seleção era quase invisível (mudava só o matiz de uma borda de 1px)
       e só existia para o mouse. O primeiro se resolve com área preenchida;
       o segundo, com um radio nativo oculto por card (ver abaixo), que
       também dá o role=radiogroup deste container. */
    <Box
      role={canSelect ? 'radiogroup' : undefined}
      aria-label="Cadastros encontrados com esse documento"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: '100%'
      }}
    >
      {records.map((record) => {
        const isSelected = record.uuid === selectedUuid
        const marcado = isSelected && canSelect

        return (
          <Box
            key={record.uuid}
            onClick={canSelect ? () => onSelect?.(record.uuid) : undefined}
            sx={{
              position: 'relative',
              p: 1.5,
              borderRadius: '6px',
              // O marcado se anuncia por área preenchida, não por matiz de
              // borda: o estado anterior mudava só a cor de 1px, a diferença
              // menos perceptível possível entre cinco cards de nome igual.
              // O hover usa neutral[900], tom diferente do preenchimento,
              // para não ficar igual ao selecionado — é o defeito do dialog
              // de Relatório (ChargeFilters.tsx:1344), onde selecionado e
              // hover pintam exatamente os mesmos dois valores.
              border: `1px solid ${
                marcado
                  ? theme.palette.primary[100]
                  : theme.palette.neutral[600]
              }`,
              backgroundColor: marcado
                ? theme.palette.primary[600]
                : 'transparent',
              transition:
                'background-color 150ms linear, border-color 150ms linear',
              ...(canSelect &&
                !marcado && {
                  '&:hover': {
                    backgroundColor: theme.palette.neutral[900],
                    borderColor: theme.palette.neutral[500]
                  }
                }),
              // O radio que dá o teclado é invisível, então o card precisa
              // mostrar o foco por conta própria.
              '&:focus-within': {
                outline: `2px solid ${theme.palette.info[300]}`,
                outlineOffset: '1px'
              },
              cursor: canSelect ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            {/* Radio nativo visualmente oculto, não um Radio do MUI: dá
                navegação por setas, foco e semântica de radiogroup de graça,
                sem ocupar espaço no card (o Dialog da library trava em
                312px). Antes disso a seleção era alcançável só pelo mouse. */}
            {canSelect && (
              <Box
                component="input"
                type="radio"
                name={radioName}
                value={record.uuid}
                checked={isSelected}
                onChange={() => onSelect?.(record.uuid)}
                aria-label={record.name || 'Cliente sem nome'}
                sx={{
                  position: 'absolute',
                  opacity: 0,
                  width: '1px',
                  height: '1px',
                  m: 0,
                  p: 0,
                  pointerEvents: 'none'
                }}
              />
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="smBold"
                sx={{ color: theme.palette.neutral[100] }}
              >
                {record.name || 'Cliente sem nome'}
              </Typography>
              <Typography
                variant="smLight"
                sx={{
                  color: theme.palette.neutral[200],
                  display: 'block'
                }}
              >
                {/* O can_create devolve o document como está gravado, que
                    pode vir cru (00947547053) ou já mascarado
                    (975.782.810-62), porque o normalize só roda no create.
                    formatCpfCnpj normaliza antes de mascarar, então os dois
                    casos saem iguais. O `||` cobre documento que não dá
                    máscara nenhuma, para não apagar o valor da tela. */}
                {formatCpfCnpj(record.document) || record.document} ·{' '}
                {STATUS_LABEL[record.status]}
              </Typography>
            </Box>

            {/* Excluido nao ganha o atalho: a tela do cliente e o unico
                lugar do painel que ainda abre um cadastro excluido (a lista
                nao tem filtro de Excluido e o Elasticsearch nao indexa esse
                status), entao o link seria a unica porta para uma pagina
                que o produto nao expoe em nenhum outro caminho.

                Ancora de verdade (nao onClick + window.open) para o
                ctrl+click e o "abrir em nova aba" do navegador
                funcionarem. O stopPropagation existe porque o card e
                clicavel quando ha mais de um registro: abrir o cadastro
                nao deve trocar a selecao por baixo do dialog. */}
            {record.status !== 'deleted' && (
              <Tooltip title="Abrir cadastro em nova aba">
                <Box
                  component="a"
                  href={`/clientes/${record.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Abrir cadastro de ${
                    record.name || 'cliente sem nome'
                  } em nova aba`}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    p: 0.5,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.neutral[800]
                    }
                  }}
                >
                  <Icon
                    colorSX={theme.palette.neutral[400]}
                    icon="faArrowUpRightFromSquare"
                    height="14px"
                    width="14px"
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default ConflictRecordList
